import React, { useEffect, useMemo, useState } from "react";
import { CoreNavLink } from "..";
import { NavLinkTypes } from "../../Definitions";
import { AttentionTypes } from "../../Definitions/valueTypes";
import { useTranslation } from "react-i18next";
import useAttentions from "../_Components/useAttentions";
import { useAppSelector } from "../../Store";
import { Graph_QL, useSystem } from "../../Services";
import { SystemMenu_Pages, focus_viewMenu_Pages } from "../../Navigation/pages";
  
interface AttentionListItem_Props {
  systemNo: number
  focus_view: boolean
  overrides?: {
    baseButton?: React.CSSProperties
    hoverStyles?: React.CSSProperties
    iconStyles?: React.CSSProperties
    hoverIconStyles?: React.CSSProperties
    contentStyles?: React.CSSProperties
    subContentStyles?: React.CSSProperties
    hoverSubContentStyles?: React.CSSProperties
    childrenStyles?: React.CSSProperties
  }
}
/**
 * AttentionCard
 * @param attentionType Defines style and header. Could be good, alert, warning or neutral.
 * @param systemContent Content to parse in top content area. 
 * @param problemContent Content to parse in bottom content area.
 * @param hover If true, the card will have opacity: "0.5" and cursor: "pointer" on onMouseEnter.
 * @param onClick Adds an onClick event to the card.
 * @returns A AttentionCard with either good, alert, warning or neutral as type.
 */
export default function AttentionListItem({ systemNo, focus_view, overrides }: AttentionListItem_Props) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  
  const { lanLogs } = useAttentions({ systemId: systemNo })

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);;

  const { call: getSystemCall, data: systemsState } = useSystem(connection); 
  
  useEffect(() => {
    getSystemCall({fetchAmount: 50})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
    // Lookup system data based on systemNo
    const { systemName, systemType, attentionType, toPath } = useMemo(() => {
      const systemTypeToPage: Record<number, SystemMenu_Pages | focus_viewMenu_Pages> = {
        1: focus_view === true ? focus_viewMenu_Pages.op_freeda_lf : SystemMenu_Pages.freeda_lf,
        2: SystemMenu_Pages.freeda_esf 
      };
    
      const systemTypeToLabel: Record<number, string> = {
        1: "Freeda Location Feeder",
        2: "Freeda ESF Walk Through"
      };
      
      const systemData = systemsState[domain]?.data.find((system) => system.system_id === systemNo);
      const page = systemData ? systemTypeToPage[systemData.fk_system_types] : undefined;
      const type = systemData ? systemTypeToLabel[systemData.fk_system_types] : undefined;
    
      // Default attentionType
      let _attentionType = AttentionTypes.neutral;
    
      if (lanLogs && lanLogs.length > 0) {
        const hasLevel0 = lanLogs.some(log => log.level === 0);
        const hasLevel1 = lanLogs.some(log => log.level === 1);
    
        if (hasLevel0) {
          _attentionType = AttentionTypes.alert;
        } else if (hasLevel1) {
          _attentionType = AttentionTypes.warning;
        }
      }
    
      return {
        systemName: systemData?.name || "",
        systemType: type || "",
        attentionType: _attentionType,
        toPath: `/${page}/${systemData?.system_id}`
      };
    }, [systemsState, domain, lanLogs, systemNo]);

  return (
    <CoreNavLink
      content={systemName}
      subContent={`${systemType}`}
      path={toPath}
      navLinkType={NavLinkTypes.attentionListItem}
      attentionType={attentionType}
      focus_view={focus_view}
    />
  )
}